/**
 * Which frames a focus presents together: chats and other frames form two
 * families that never share a focus, and at most four frames show.
 */
import { MAX_FOCUS_FRAMES } from "@/lib/focus-window-layout";
import {
  isConversationFrame,
  type FocusPlanEnv,
} from "@/lib/focus/focus-plan-layout";

/** The newest frames up to the cap, always keeping the active one. */
function cappedFocusIds(frameIds: string[], activeId: string): string[] {
  if (frameIds.length <= MAX_FOCUS_FRAMES) return frameIds;
  const newestIds = frameIds.slice(-MAX_FOCUS_FRAMES);
  if (newestIds.includes(activeId)) return newestIds;
  const retainedIds = new Set([
    activeId,
    ...frameIds.slice(-(MAX_FOCUS_FRAMES - 1)),
  ]);
  return frameIds.filter((id) => retainedIds.has(id));
}

export function focusFamilyIds(
  frameIds: string[],
  activeId: string,
  options: {
    /** A conversation companion stack keeps every artifact as a title. */
    companionStack?: boolean;
    preserveMixedFrameTypes?: boolean;
  },
  env: FocusPlanEnv,
): string[] {
  const activeIsConversation = isConversationFrame(env, activeId);
  const familyIds = options.preserveMixedFrameTypes
    ? frameIds
    : frameIds.filter(
        (id) => isConversationFrame(env, id) === activeIsConversation,
      );
  if (options.companionStack) return familyIds;
  return options.preserveMixedFrameTypes || activeIsConversation
    ? cappedFocusIds(familyIds, activeId)
    : [activeId, ...familyIds.filter((id) => id !== activeId)].slice(
        0,
        MAX_FOCUS_FRAMES,
      );
}
